/**
 * TanStack Router route tree with role-based access guards.
 * Code-based routing (SD-001) — no file-based router plugin needed.
 */

import {
  createRootRouteWithContext,
  createRoute,
  redirect,
} from "@tanstack/react-router";
import type { UserRole } from "@/hooks/use-role";
import { AppShell } from "@/components/layout/app-shell";
import { LoginPage } from "@/pages/login";
import { NotFoundPage } from "@/pages/not-found";
import { DashboardPage } from "@/pages/dashboard";
import { CustomersPage } from "@/pages/customers";
import { CustomerDetailPage } from "@/pages/customer-detail";
import { TechniciansPage } from "@/pages/technicians";
import { TechnicianDetailPage } from "@/pages/technician-detail";
import { PimSessionsPage } from "@/pages/pim-sessions";
import { PimApprovalsPage } from "@/pages/pim-approvals";
import { ApprovalPoliciesPage } from "@/pages/approval-policies";
import { AuditLogPage } from "@/pages/audit-log";
import { GdapRelationshipsPage } from "@/pages/gdap-relationships";
import { GdapDetailPage } from "@/pages/gdap-detail";
import { SettingsPage } from "@/pages/settings";
import { ScheduledTasksPage } from "@/pages/scheduled-tasks";
import { ActivateSessionPage } from "@/pages/activate-session";

// Page placeholders for pages not yet built
import { GlassCard } from "@/components/sc/glass-card";

export interface RouterContext {
  isAuthenticated: boolean;
  role: UserRole;
}

// Shared role guard function
function checkRole(
  allowedRoles: readonly UserRole[],
  role: UserRole,
): void {
  if (allowedRoles.includes(role)) return;

  // Auditor always goes to audit-log
  if (role === "auditor") {
    throw redirect({ to: "/audit-log" });
  }
  // Technician goes to dashboard
  throw redirect({ to: "/dashboard" });
}

// ---- Root Route ----
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: NotFoundPage,
});

// ---- Login Route (no auth required) ----
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: ({ context }) => {
    if (context.isAuthenticated) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: LoginPage,
});

// ---- Layout Route (authenticated shell) ----
export const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  beforeLoad: ({ context }) => {
    if (!context.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: AppShell,
});

// ---- Index redirect ----
export const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

// ---- Dashboard ----
export const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/dashboard",
  beforeLoad: ({ context }) => {
    if (context.role === "auditor") {
      throw redirect({ to: "/audit-log" });
    }
  },
  component: DashboardPage,
});

// ---- Customers ----
export const customersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/customers",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: CustomersPage,
});

export const customerDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/customers/$customerId",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: CustomerDetailPage,
});

export const customerActivateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/customers/$customerId/activate",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: ActivateSessionPage,
});

// ---- Technicians (admin only) ----
export const techniciansRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/technicians",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: TechniciansPage,
});

export const technicianDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/technicians/$technicianId",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: TechnicianDetailPage,
});

// ---- PIM Sessions ----
export const pimSessionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/pim-sessions",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: PimSessionsPage,
});

// Placeholder for session detail page
function SessionDetailPlaceholder() {
  return (
    <GlassCard>
      <h2 className="text-h2 text-sc-navy font-display mb-2">
        Session Detail
      </h2>
      <p className="text-body-sm text-sc-text-muted">
        Session information and actions will be rendered here.
      </p>
    </GlassCard>
  );
}

export const sessionDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/pim-sessions/$sessionId",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: SessionDetailPlaceholder,
});

// ---- PIM Approvals (admin + technician) ----
export const pimApprovalsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/pim-approvals",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "technician"], context.role);
  },
  component: PimApprovalsPage,
});

// ---- Approval Policies (admin only) ----
export const approvalPoliciesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/approval-policies",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: ApprovalPoliciesPage,
});

// ---- GDAP Relationships (admin only) ----
export const gdapRelationshipsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/gdap",
  validateSearch: (search: Record<string, unknown>) => ({
    createFor: typeof search.createFor === "string" ? search.createFor : undefined,
  }),
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: GdapRelationshipsPage,
});

export const gdapDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/gdap/$relationshipId",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: GdapDetailPage,
});

// ---- Audit Log (admin + auditor) ----
export const auditLogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/audit-log",
  beforeLoad: ({ context }) => {
    checkRole(["admin", "auditor"], context.role);
  },
  component: AuditLogPage,
});

// ---- Scheduled Tasks (admin only) ----
export const scheduledTasksRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/scheduled-tasks",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: ScheduledTasksPage,
});

// ---- Settings (admin only) ----
export const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings",
  beforeLoad: ({ context }) => {
    checkRole(["admin"], context.role);
  },
  component: SettingsPage,
});

// ---- Route Tree ----
export const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    customersRoute,
    customerDetailRoute,
    customerActivateRoute,
    techniciansRoute,
    technicianDetailRoute,
    pimSessionsRoute,
    sessionDetailRoute,
    pimApprovalsRoute,
    approvalPoliciesRoute,
    gdapRelationshipsRoute,
    gdapDetailRoute,
    auditLogRoute,
    scheduledTasksRoute,
    settingsRoute,
  ]),
]);
